export function isEmbedded(): boolean {
  try {
    return window.self !== window.top || new URLSearchParams(window.location.search).get('embed') === '1';
  } catch {
    return true; // safer default when cross-origin blocks access
  }
}

export const BRIDGE_CHANNEL = 'melp-mail-bridge-v1';

export type BridgeMessage = {
  channel: typeof BRIDGE_CHANNEL;
  type:
    | 'ready'
    | 'layoutRecommendation'
    | 'logout'
    | 'navigate'
    | 'focusMailbox'
    | 'compose'
    | 'reply'
    | 'replyAll';
  payload?: any;
};

// Comma-separated list of origins this webmail is allowed to be embedded in
// (e.g. the admin portal). Parsed once at module load. We use this both as the
// targetOrigin for outgoing postMessages and as an allowlist for incoming ones.
const allowedParentOrigins: string[] = (import.meta.env.VITE_ALLOWED_PARENT_ORIGINS ?? '')
  .split(',')
  .map((o: string) => o.trim())
  .filter(Boolean);

export function postToParent(msg: Omit<BridgeMessage, 'channel'>) {
  if (!isEmbedded()) return;
  const full: BridgeMessage = { channel: BRIDGE_CHANNEL, ...msg };
  // Send one copy per configured parent. Browsers only deliver to the actual
  // parent window, so non-matching origins are silently ignored. If no parents
  // are configured, fall back to same-origin (still safer than '*').
  const targets = allowedParentOrigins.length > 0 ? allowedParentOrigins : [window.location.origin];
  for (const origin of targets) {
    try {
      window.parent.postMessage(full, origin);
    } catch {
      // postMessage with mismatched origin throws on some browsers - ignore.
    }
  }
}

export function initParentBridge(options?: { onLogout?: () => void }) {
  if (!isEmbedded()) return () => {};

  const onMessage = (ev: MessageEvent) => {
    // Only trust bridge messages from configured parent origins.
    if (allowedParentOrigins.length > 0 && !allowedParentOrigins.includes(ev.origin)) return;
    const data = ev.data as BridgeMessage;
    if (!data || data.channel !== BRIDGE_CHANNEL) return;
    switch (data.type) {
      case 'logout':
        options?.onLogout?.();
        break;
      default:
        break;
    }
  };

  window.addEventListener('message', onMessage);

  // Announce readiness and suggest host hide its own header
  postToParent({ type: 'ready', payload: { version: 1 } });
  postToParent({ type: 'layoutRecommendation', payload: { hideHostHeader: true } });

  return () => window.removeEventListener('message', onMessage);
}

